import _ from "lodash";
import React from "react";
import { shallowEqual } from "react-redux";
import i18n from "src/i18n/config";
import { RootState, useSelector } from "src/store";
import { z } from "zod";

import { selectDevicesByGroupIds } from "@dashboard/devices/store/selectors/devices";
import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";

import { AddedDeviceType } from "./AddedDevice";

export type QrCodeContent = {
    serial: string;
    mac?: string;
    model?: string;
};

// The key name, including the space, is the one printed on JKC's device packages.
const qrCodeSchema = z.object({
    serial: z.string(),
    mac: z.string().optional(),
    "model type": z.string().optional(),
});

export const parseQrCodeValue = (rawValue: string): QrCodeContent => {
    const trimmed = rawValue.trim();
    try {
        const parsed = JSON.parse(trimmed);
        const result = qrCodeSchema.parse(parsed);
        return { serial: result.serial.trim(), mac: result.mac?.trim(), model: result["model type"] };
    } catch {
        return { serial: trimmed };
    }
};

export type ProductResolution = { codename: string } | { error: string; errorToast: string };

export const resolveProduct = (model: string | undefined, products: API.VisibleProduct[]): ProductResolution => {
    const name = model?.trim().toLowerCase();
    if (!name) {
        return {
            error: i18n.t("modals:addDevice.scanError.modelMissing"),
            errorToast: i18n.t("modals:addDevice.scanError.modelMissingSelectManually"),
        };
    }

    const matches = products.filter((product) => product.productName.trim().toLowerCase() === name);
    if (matches.length === 0) {
        return {
            error: i18n.t("modals:addDevice.scanError.modelUnknown", { model: model?.trim() }),
            errorToast: i18n.t("modals:addDevice.scanError.modelUnknownSelectManually", { model: model?.trim() }),
        };
    }
    if (matches.length > 1) {
        return {
            error: i18n.t("modals:addDevice.scanError.modelAmbiguous", { model: model?.trim() }),
            errorToast: i18n.t("modals:addDevice.scanError.modelAmbiguousSelectManually", { model: model?.trim() }),
        };
    }
    return { codename: matches[0].codename };
};

export const serialSchema = z
    .string({ message: i18n.t("common:validation.fieldRequired") })
    .trim()
    .regex(/^[a-zA-Z0-9]+$/, { message: i18n.t("common:validation.deviceSerialAlphanumeric") })
    .min(8, { message: i18n.t("common:validation.deviceSerialMinLength") });

export const macAddressPattern = /^[0-9a-f]{2}([:-])[0-9a-f]{2}(\1[0-9a-f]{2}){4}$/i;

export const normalizeMacAddress = (mac: string | undefined) => mac?.trim().toLowerCase().replaceAll("-", ":") || undefined;

export const schema = z.object({
    androidSerial: serialSchema,
    macAddress: z
        .string()
        .trim()
        .refine((value) => value === "" || macAddressPattern.test(value), {
            message: i18n.t("common:validation.macAddressInvalid"),
        })
        .optional(),
    productName: z.string({ message: i18n.t("common:validation.fieldRequired") }),
    groupId: z.number({ message: i18n.t("common:validation.fieldRequired") }),
});

export const makeSchema = (isEnrolled: (serial: string) => boolean) =>
    schema.extend({
        androidSerial: serialSchema.refine((serial) => !isEnrolled(serial), {
            message: i18n.t("modals:addDevice.toast.deviceAlreadyExists"),
        }),
    });

export const useDevices = () => {
    const { data: currentSubscription } = useCurrentSubscription({});
    const groupIds = currentSubscription?.groupIds || [];
    const storeDevices = useSelector((state: RootState) => selectDevicesByGroupIds(state, groupIds), shallowEqual);
    const [addedDevices, setAddedDevices] = React.useReducer(
        (state: AddedDeviceType[], action: AddedDeviceType[]) => _.orderBy([...state, ...action], "createdAt", ["desc"]),
        [],
    );

    return { addedDevices, setAddedDevices, devices: storeDevices };
};
